import { showError, showHttpError, showSuccess } from '@calendly/alert'
import { now } from '@calendly/datetime'
import { buildQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { useLayoutEffect } from 'react'

import { upgradeSubscription } from 'components/billing/use_upgrade_subscription'
import {
  EVENT_TYPE_LIST_TAGS,
  FETCH_EVENT_TYPE,
  MANAGED_EVENT_TYPE,
} from 'constants/event_types'
import { AVAILABILITY_INVALIDATION_TAGS } from 'features/availability'
import { isAvailableFeatureSelector } from 'features/billing'
import { MET_INVALIDATION_TAGS } from 'features/managed_event_types'
import { updateEventType as schedulingUpdateEventType } from 'features/scheduling'
import { rootApi } from 'root-api'
import { getFeatureKeyFromError } from 'services/billing'
import { basePath, memberPath } from 'services/event_type_path_utils'
import {
  apiDeleteWorkflowUrl,
  apiEventTypesWorkflowUrl,
  apiManagedWorkflowsUrl,
  apiOwnerListUrl,
  apiUpdateWorkflowUrl
} from 'services/workflows/url.js'

import { updateEventTypeListsCacheData, updateWorkflowsCacheData } from './cache_updaters'
import { MAX_PAGE_SIZE } from './constants'
import { convertETItemToETListItem, entityToSuggestion, getETPickerProfileEventTypesUrl, workflowsState } from './utils'

export const updateEventTypeCacheData = ({ api, state, dispatch, data }) => {
  const entriesToUpdate = api.util.selectInvalidatedBy(state, ['EVENT_TYPES'])
  const argsToPatch = new Map()

  entriesToUpdate.forEach(({ endpointName, originalArgs }) => {
    if (endpointName === FETCH_EVENT_TYPE && String(originalArgs?.id) === String(data.id)) {
      argsToPatch.set(JSON.stringify(originalArgs), originalArgs)
    }
  })

  // When nothing matched (e.g. skipInvalidation so EVENT_TYPES wasn't invalidated yet, or
  // selectInvalidatedBy returned unrelated entries), patch using args from the response.
  if (argsToPatch.size === 0 && data?.id != null) {
    const syntheticArgs = {
      id: data.id,
      type: data.type || 'StandardEventType',
      ownerType: data.owner_type ?? data.ownerType ?? 'User',
      ownerId: data.owner_id ?? data.ownerId ?? 'me'
    }
    argsToPatch.set(JSON.stringify(syntheticArgs), syntheticArgs)
  }

  argsToPatch.forEach((originalArgs) => {
    dispatch(
      api.util.updateQueryData(FETCH_EVENT_TYPE, originalArgs, (draft) => {
        Object.assign(draft, data)
      })
    )
  })
}

const getTypes = (tags) => tags.map((tag) => tag.type)

const api = rootApi.enhanceEndpoints({ addTagTypes: [
  ...EVENT_TYPE_LIST_TAGS,
  ...getTypes(AVAILABILITY_INVALIDATION_TAGS),
  'WORKFLOWS'
] }).injectEndpoints({
  endpoints: (build) => ({
    createEventType: build.mutation({
      query: ({ eventType, attributes }) => ({
        url: basePath(eventType),
        method: 'POST',
        data: attributes
      }),
      invalidatesTags: EVENT_TYPE_LIST_TAGS.concat(AVAILABILITY_INVALIDATION_TAGS, MET_INVALIDATION_TAGS.CREATE_EVENT_TYPE),
      extraOptions: {
        // When we get everything ported over to RTK query, we can remove this,
        // but for now, the camelCase breaks something
        transformRequest: false,
        transformResponse: false
      },
      onQueryStarted: async ({ onSuccess }, { queryFulfilled, dispatch }) => {
        try {
          const { data: response } = await queryFulfilled

          dispatch(showSuccess(I18n.t('publisher.event_type_editor.saved')))
          if (onSuccess) { onSuccess(response) }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    updateEventType: build.mutation({
      query: ({ eventType, attributes }) => ({
        url: memberPath(eventType),
        method: 'PATCH',
        data: attributes
      }),
      extraOptions: {
        // When we get everything ported over to RTK query, we can remove this,
        // but for now, the camelCase breaks something
        transformRequest: false,
        transformResponse: false,
        handleErrors: false
      },
      invalidatesTags: (_result, error, { skipInvalidation = true, eventType: { type: etType } }) => {
        const isManaged = etType === MANAGED_EVENT_TYPE

        if (isManaged) return ['MANAGED_EVENT_TYPE_LIST', 'ROUTING_FORM']

        // Separate AVAILABILITY_INVALIDATION_TAGS from other invalidations
        const baseInvalidations = (error || skipInvalidation) ? [] : EVENT_TYPE_LIST_TAGS.concat('ROUTING_FORM')
        return baseInvalidations.concat(AVAILABILITY_INVALIDATION_TAGS)
      },
      onQueryStarted: async (queryAttributes, { queryFulfilled, dispatch, getState }) => {
        const {
          eventType,
          onSuccess,
          skipInvalidation = true,
          successMessage = I18n.t('publisher.event_type_editor.saved'),
        } = queryAttributes

        const isManaged = eventType.type === MANAGED_EVENT_TYPE

        try {
          const { data: response } = await queryFulfilled

          const state = getState()

          // The Managed Events page does not use `state.eventTypes` so
          // we don't want to call these methods
          if (!isManaged && skipInvalidation) {
            const data = convertETItemToETListItem(response)

            dispatch(schedulingUpdateEventType({ id: data.id, data }))

            updateEventTypeListsCacheData({ api, state, dispatch, data })
          }

          // Always merge full PATCH response into fetchEventType cache so partial updates
          // (e.g. locale only) still refresh translated name, calendar invitation copy, etc.
          updateEventTypeCacheData({ api, state, dispatch, data: response })
          dispatch(showSuccess(successMessage))
          if (onSuccess) { onSuccess(response) }
        } catch (e) {
          if (!['request_aborted', 'validation'].includes(e.error?.type)) {
            const featureKey = getFeatureKeyFromError(e.error)

            if (featureKey) {
              upgradeSubscription({
                dispatch,
                featureKey,
                onSuccess: () => dispatch(
                  api.endpoints.updateEventType.initiate(queryAttributes, { fixedCacheKey: `update-event-type-${eventType.id}` })
                )
              })
            } else {
              dispatch(showError(e.error.message))
            }
          } else if (e.error?.type === 'request_aborted') {
            dispatch(showHttpError(e.error))
          } else {
            dispatch(showError(I18n.t('publisher.event_type_editor.sections.errors.section')))
          }
        }
      }
    }),
    saveEventTypeInternalNote: build.mutation({
      query: ({ eventType, internalNote }) => ({
        url: `${memberPath(eventType)}/internal_note`,
        method: 'PUT',
        data: { internalNote }
      }),
      extraOptions: {
        transformResponse: false
      },
      onQueryStarted: async ({ eventType, internalNote }, { queryFulfilled, dispatch, getState }) => {
        try {
          const { data: response } = await queryFulfilled

          const state = getState()
          const listData = convertETItemToETListItem({ ...eventType, internal_note: response?.internal_note })

          updateEventTypeListsCacheData({ api, state, dispatch, data: listData })
          updateEventTypeCacheData({ api, state, dispatch, data: { id: eventType.id, internal_note: response?.internal_note } })
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    activateEventType: build.mutation({
      query: ({ eventType }) => ({
        url: `${memberPath(eventType)}/activate`,
        method: 'PUT'
      }),
      invalidatesTags: ['WIZARD_METADATA'],
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ eventType }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled

          const state = getState()
          const listData = convertETItemToETListItem({ ...eventType, active: true })

          updateEventTypeListsCacheData({ api, state, dispatch, data: listData })
          updateEventTypeCacheData({ api, state, dispatch, data: { id: eventType.id, active: true } })
        } catch (e) {
          const featureKey = getFeatureKeyFromError(e.error)
          if (!featureKey) {
            dispatch(showHttpError(e.error))
            return
          }

          upgradeSubscription({
            dispatch,
            featureKey,
            onSuccess: () => dispatch(api.endpoints.activateEventType.initiate({ eventType }))
          })
        }
      }
    }),
    deactivateEventType: build.mutation({
      query: ({ eventType }) => ({
        url: `${memberPath(eventType)}/deactivate`,
        method: 'PUT'
      }),
      invalidatesTags: ['WIZARD_METADATA'],
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ eventType }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled

          const state = getState()
          const listData = convertETItemToETListItem({ ...eventType, active: false })

          updateEventTypeListsCacheData({ api, state, dispatch, data: listData })
          updateEventTypeCacheData({ api, state, dispatch, data: { id: eventType.id, active: false } })
        } catch (e) {
          const featureKey = getFeatureKeyFromError(e.error)
          if (!featureKey) {
            dispatch(showHttpError(e.error))
            return
          }

          upgradeSubscription({
            dispatch,
            featureKey,
            onSuccess: () => dispatch(api.endpoints.deactivateEventType.initiate({ eventType }))
          })
        }
      }
    }),
    searchForUserOwnerEventTypes: build.query({
      query: ({ eventTypeId, filterTerm, extraParams, userId }) => {
        const baseParams = {
          filter_term: filterTerm,
          scope: 'my_calendly',
          only_hosts: true,
          ...(eventTypeId ? { skipping_event_type_ids: [eventTypeId] } : {})
        }
        const buildQueryParams = extraParams ? { ...extraParams, ...baseParams } : baseParams

        return {
          url: `/api/users/${userId}/event_types?${buildQuery(buildQueryParams)}`
        }
      },
    }),
    fetchMoreEventTypesEventTypes: build.query({
      query: (params) => {
        const { page, filterTerm, eventTypeId, extraParams } = params
        const baseParams = {
          filter_term: filterTerm,
          only_hosts: true,
          page,
          format: 'collection',
          ...(eventTypeId ? { skipping_event_type_ids: [eventTypeId] } : {})
        }
        const buildQueryParams = extraParams ? { ...extraParams, ...baseParams } : baseParams

        return {
          url: getETPickerProfileEventTypesUrl(params),
          params: buildQueryParams
        }
      },
      keepUnusedDataFor: 0,
      onQueryStarted: async ({ profileType, profileId, scopeParams }, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled
          const eventTypes = response.results ? response.results : Object.values(response)

          if (eventTypes && eventTypes.length) {
            dispatch(api.util.updateQueryData('searchForUserOwnerEventTypes', scopeParams, (draft) => {
              const index = draft.results.findIndex(({ id, type }) => type === profileType && id === profileId)
              draft.results[index].nextPage = response.pagination.nextPage
              draft.results[index].eventTypes.push(...eventTypes)
            }))
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    searchEventTypeHosts: build.query({
      extraOptions: {
        transformResponse: false
      },
      queryFn: async ({ query, searchMembershipsOnly = false, searchForGroupAdmin = false }, _api, _extraOptions, fetchWithBaseQuery) => {
        const params = {
          sort_order: 'asc',
          page_size: MAX_PAGE_SIZE,
          filter_for_group_admin: searchForGroupAdmin,
          ...(query ? { filter_term: query } : {})
        }

        if (searchMembershipsOnly) {
          const { data } = await fetchWithBaseQuery({
            url: '/api/organization/memberships',
            params: { ...params, sort_field: 'name' }
          })

          if (data.error) return { error: data.error }

          return {
            data: {
              suggestions: data.results.map((membership) => entityToSuggestion(membership, 'organization_membership')),
              query
            }
          }
        }

        const promises = [
          fetchWithBaseQuery({
            url: '/api/organization/memberships',
            params: { ...params, sort_field: 'name' }
          }),
          fetchWithBaseQuery({
            url: '/api/organization/invitations',
            params: { ...params, status: 'pending', sort_field: 'email' }
          })
        ]
        const data = await Promise.all(promises)

        const error = data.find((d) => d.error)
        if (error) return { error }

        return {
          data: {
            suggestions: [
              ...data[0].data.results.map((membership) => entityToSuggestion(membership, 'organization_membership')),
              ...data[1].data.results.map((invitation) => entityToSuggestion(invitation, 'organization_invitation'))
            ],
            query
          }
        }
      }
    }),
    fetchUserCalendarAccountsConnected: build.query({
      query: (userUuid) => ({
        url: `/api/users/${userUuid}/calendar_accounts/connected`
      })
    }),
    applyAvailabilityToEventTypes: build.mutation({
      query: ({ userId, sourceEventTypeId, eventTypeIds }) => ({
        url: `/api/availability/event_types/${sourceEventTypeId}/apply_availability_rules`,
        method: 'POST',
        data: { targetEventTypeIds: eventTypeIds, userId }
      })
    }),
    fetchDoubleBookingSuggestions: build.query({
      query: () => ({
        url: '/api/user_busy_times',
        method: 'GET',
        params: {
          start_time: now().set({ second: 0, millisecond: 0 }).toISO(),
          end_time: now().plus({ days: 60 }).toFormat('yyyy-MM-dd'),
          filter_events: true
        }
      })
    }),
    fetchLegacyWorkflowsForEventType: build.query({
      extraOptions: {
        transformResponse: false
      },
      queryFn: async ({ type, ownerType, ownerId, eventTypeId }, { dispatch, getState }, _extraOptions, fetchWithBaseQuery) => {
        const isWorkflowsAvailable = isAvailableFeatureSelector(getState(), { featureKey: 'workflows' })

        if (!isWorkflowsAvailable) {
          return { data: { state: 'hero_banner' } }
        }

        const { data, error } = await fetchWithBaseQuery({
          url: apiEventTypesWorkflowUrl(type, ownerType, ownerId, eventTypeId)
        })

        if (error) {
          if (data.type === 'policy') {
            return { data: { state: 'hero_banner' } }
          }

          dispatch(showHttpError(data))
          return { data: { state: 'failed' } }
        }

        return {
          data: {
            state: workflowsState(data),
            list: data.workflows.collection,
            canManageWorkflows: data.can_manage_workflows
          }
        }
      },
      providesTags: ['WORKFLOWS']
    }),
    fetchWorkflowsForEventType: build.query({
      extraOptions: {
        transformResponse: false
      },
      queryFn: async ({
        type,
        ownerType,
        ownerId,
        eventTypeId,
        parentId,
        isMETParent,
        withAdminManaged
      }, { getState, dispatch }, _extraOptions, fetchWithBaseQuery) => {
        const isWorkflowsAvailable = isAvailableFeatureSelector(getState(), { featureKey: 'workflows' })

        if (!isWorkflowsAvailable) {
          return { data: { state: 'hero_banner' } }
        }

        let success = true
        let responseData = null
        let workflows = null

        if (isMETParent) {
          const { data, error } = await fetchWithBaseQuery({
            url: apiManagedWorkflowsUrl
          })

          if (error) {
            success = false
            responseData = data
          } else {
            const collection = data.collection || []
            const list = collection.filter((workflow) => {
              if (!workflow.event_types || !Array.isArray(workflow.event_types)) {
                return false
              }

              return workflow.event_types.some((et) => {
                // Prevent null parentId from matching null event type IDs
                if (parentId === null && et.id === null) {
                  return false
                }
                return String(et.id) === String(eventTypeId) || String(et.id) === String(parentId)
              })
            })
            workflows = {
              list,
              state: list.length > 0 ? 'list' : 'empty_state',
              // There is an additional check in advanced_notifications_section
              canManageWorkflows: true
            }
          }
        } else {
          const { data, error } = await fetchWithBaseQuery({
            url: apiOwnerListUrl,
            params: {
              owner_id: ownerId,
              owner_type: ownerType.toLowerCase(),
              event_type_id: eventTypeId,
              ...(withAdminManaged && { with_admin_managed: true }),
            },
            method: 'GET'
          })

          if (error) {
            success = false
            responseData = data
          } else {
            const ownerData = data.collection[0]
            workflows = {
              state: workflowsState(ownerData),
              list: ownerData.workflows.collection,
              canManageWorkflows: ownerData.can_manage_workflows,
            }
          }
        }

        if (!success) {
          if (responseData.type === 'policy') {
            return { data: { state: 'hero_banner' } }
          }

          dispatch(showHttpError(responseData))
          return { data: { state: 'failed' } }
        }

        return { data: workflows }
      },
      providesTags: ['WORKFLOWS']
    }),
    deleteWorkflow: build.mutation({
      query: ({ workflow, workflowsExtractionEnabled }) => ({
        url: apiDeleteWorkflowUrl({ type: workflow.owner_type, id: workflow.owner_id }, workflow.uuid, { workflowsExtractionEnabled }),
        method: 'DELETE'
      }),
      onQueryStarted: async ({ onSuccess, workflowsExtractionEnabled }, { queryFulfilled, dispatch, getState }) => {
        try {
          const { data } = await queryFulfilled
          const state = getState()

          updateWorkflowsCacheData({ api, state, dispatch, data, workflowsExtractionEnabled })

          if (onSuccess) { onSuccess() }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    removeEventTypeFromWorkflow: build.mutation({
      query: ({ workflow, eventTypeId, workflowsExtractionEnabled }) => ({
        url: apiUpdateWorkflowUrl({ id: workflow.owner_id, type: workflow.owner_type }, workflow.uuid, { workflowsExtractionEnabled }),
        method: 'PATCH',
        data: {
          uuid: workflow.uuid,
          event_type_ids: workflow.event_types.filter(({ id }) => String(id) !== String(eventTypeId)).map(({ id }) => id)
        }
      }),
      onQueryStarted: async ({ onSuccess, workflowsExtractionEnabled }, { queryFulfilled, dispatch, getState }) => {
        try {
          const { data } = await queryFulfilled
          const state = getState()

          updateWorkflowsCacheData({ api, state, dispatch, data, workflowsExtractionEnabled })

          if (onSuccess) { onSuccess() }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
  })
})

export const {
  useActivateEventTypeMutation,
  useApplyAvailabilityToEventTypesMutation,
  useCreateEventTypeMutation,
  useDeactivateEventTypeMutation,
  useDeleteWorkflowMutation,
  useFetchDoubleBookingSuggestionsQuery,
  useFetchLegacyWorkflowsForEventTypeQuery,
  useFetchUserCalendarAccountsConnectedQuery,
  useFetchWorkflowsForEventTypeQuery,
  useLazyFetchMoreEventTypesEventTypesQuery,
  useLazySearchForUserOwnerEventTypesQuery,
  useRemoveEventTypeFromWorkflowMutation,
  useSaveEventTypeInternalNoteMutation,
  useSearchEventTypeHostsQuery
} = api

export const useUpdateEventTypeMutation = ({ id, fixedCacheKey }) => api.useUpdateEventTypeMutation({
  fixedCacheKey: fixedCacheKey ?? `update-event-type-${id}`
})

// resets the mutation cache so that the error state is cleared
//   when the user navigates back to the event type
export const useResetUpdateMutationsCache = ({ id }) => {
  const [, { reset: resetETMutationCache }] = useUpdateEventTypeMutation({ fixedCacheKey: `update-event-type-${id}` })

  useLayoutEffect(() => {
    resetETMutationCache()
  }, [resetETMutationCache])
}
